// External libraries
import type { SerializedStyles } from '@emotion/core';
import { css } from '@emotion/core';

// Internal modules
import {
  bodyMediumStyles,
  DEFAULT_BORDER_COLOR,
  DISABLED_BACKGROUND_COLOR,
  ERROR_COLOR,
  PRIMARY_COLOR_V2,
  REBRAND_ERROR_COLOR,
  REBRAND_T1_PRIMARY_COLOR,
  REBRAND_T1_SELECTION_COLOR,
} from '@/styles';

const getRadioBoxShadow = ({
  displayError,
  disabled,
  shouldUsePayPalRebrandStyles,
}: {
  displayError?: boolean;
  disabled?: boolean;
  shouldUsePayPalRebrandStyles: boolean;
}) => {
  if (disabled) {
    return `inset 0 0 0 100px ${DISABLED_BACKGROUND_COLOR}`;
  }

  const BORDER_ERROR_COLOR = shouldUsePayPalRebrandStyles
    ? REBRAND_ERROR_COLOR
    : ERROR_COLOR;
  return `inset 0 0 0 2px ${
    displayError ? BORDER_ERROR_COLOR : DEFAULT_BORDER_COLOR
  }`;
};

const getFocusBoxShadow = (
  shouldUsePayPalRebrandStyles: boolean,
  displayError: boolean,
) => {
  if (displayError && shouldUsePayPalRebrandStyles) {
    return `inset 0 0 0 2px ${REBRAND_ERROR_COLOR}, inset 0 0 0 8px ${REBRAND_T1_PRIMARY_COLOR}`;
  }
  const BOX_SHADOW_COLOR = shouldUsePayPalRebrandStyles
    ? REBRAND_T1_PRIMARY_COLOR
    : PRIMARY_COLOR_V2;
  return `inset 0 0 0 8px ${BOX_SHADOW_COLOR}`;
};

export const getCommonRadioButtonStyleOverrides = ({
  displayError,
  disabled,
  labelFontSize,
  radioButtonContainerMargin,
  shouldUsePayPalRebrandStyles,
}: {
  /** Is an error surfaced? */
  displayError?: boolean;
  /** Is this component disabled? */
  disabled?: boolean;
  /** Expects a css font-size value and applies it to the label styles of the radio button. */
  labelFontSize?: string;
  /** Expects a css margin value and applies it to the container styles of the radio button. */
  radioButtonContainerMargin?: string;
  /** Whether we should use the newest version of PayPal coloring/styles. */
  shouldUsePayPalRebrandStyles: boolean;
}): { [key: string]: SerializedStyles } => {
  const BOX_SHADOW_COLOR = shouldUsePayPalRebrandStyles
    ? REBRAND_T1_PRIMARY_COLOR
    : PRIMARY_COLOR_V2;
  const OUTLINE_COLOR = shouldUsePayPalRebrandStyles
    ? REBRAND_T1_SELECTION_COLOR
    : PRIMARY_COLOR_V2;

  const overrides = {
    containerStyleOverrides: css`
      margin: ${radioButtonContainerMargin};
    `,

    inputStyleOverrides: css`
      &:focus + label::before {
        box-shadow: inset 0 0 0 8px ${BOX_SHADOW_COLOR};
        outline: 2px solid ${OUTLINE_COLOR};
        outline-offset: 2px;
        border: 2px solid inset ${REBRAND_ERROR_COLOR};
      }

      &:checked + label::before {
        box-shadow: ${getFocusBoxShadow(
          shouldUsePayPalRebrandStyles,
          Boolean(displayError),
        )};
      }
    `,

    labelStyleOverrides: css`
      cursor: ${disabled ? 'default' : 'pointer'};
      font-weight: 400;
      font-size: ${labelFontSize};
      min-width: 100px;
      line-height: 24px;

      &::before {
        box-shadow: ${getRadioBoxShadow({
          disabled,
          displayError,
          shouldUsePayPalRebrandStyles,
        })};
      }

      .overrides-font-4-2 & {
        ${bodyMediumStyles}
      }
    `,
  };
  return overrides;
};
